import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

/**
 * Reusable Category -> Sub-template selector.
 * When a category is selected, the category's default template is auto-selected.
 * Calls onChange(templateId, templateObj) whenever the selected template changes.
 */
export default function TemplateSelect({ value, onChange }) {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/template-categories');
        setCategories(res.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const loadTemplates = useCallback(async (catId, autoSelectDefault) => {
    setLoadingTemplates(true);
    try {
      const res = await api.get('/templates', { params: { category_id: catId } });
      const list = res.data || [];
      setTemplates(list);
      if (autoSelectDefault) {
        const def = list.find(t => t.is_default) || list[0];
        onChange(def ? def.id : '', def || null);
      }
    } catch { setTemplates([]); }
    finally { setLoadingTemplates(false); }
  }, [onChange]);

  const handleCategory = (catId) => {
    setCategoryId(catId);
    loadTemplates(catId, true);
  };

  const handleTemplate = (tid) => {
    const t = templates.find(x => x.id === tid) || null;
    onChange(tid, t);
  };

  return (
    <div className="space-y-3" data-testid="template-select">
      <div>
        <label className="text-xs text-[#6B7280] uppercase tracking-wide">Category</label>
        <Select value={categoryId} onValueChange={handleCategory}>
          <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]" data-testid="template-category-select">
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent style={{ pointerEvents: 'auto', zIndex: 9999 }}>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {categoryId && (
        <div>
          <label className="text-xs text-[#6B7280] uppercase tracking-wide">Sub-template</label>
          <Select value={value || ''} onValueChange={handleTemplate} disabled={loadingTemplates || templates.length === 0}>
            <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]" data-testid="template-subtemplate-select">
              <SelectValue placeholder={loadingTemplates ? 'Loading…' : (templates.length === 0 ? 'No templates available' : 'Select a template')} />
            </SelectTrigger>
            <SelectContent style={{ pointerEvents: 'auto', zIndex: 9999 }}>
              {templates.map(t => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}{t.is_default ? '  ★' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {templates.length === 0 && !loadingTemplates && (
            <p className="text-[11px] text-[#6B7280] mt-1">No templates in this category yet.</p>
          )}
          {value && (() => {
            const sel = templates.find(t => t.id === value);
            if (!sel) return null;
            const roomCount = (sel.rooms || []).length;
            const itemCount = (sel.rooms || []).reduce((s, r) => s + (r.items?.length || 0), 0);
            return (
              <div className="flex items-center gap-2 mt-2">
                {sel.is_default && <Badge className="bg-[#3A8C8C]/10 text-[#3A8C8C] text-[9px]">Default</Badge>}
                <span className="text-[11px] text-[#6B7280]">{roomCount} rooms · {itemCount} items</span>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
